import type { ContextTag } from './context-badges.js';
import { panelNavHtml } from '../panel-nav-html.js';

export interface CallSummaryData {
  startedAt?: Date;
  endedAt?: Date;
  context: ContextTag[];
  captures: Array<{ slot: string; value: string }>;
  bookingReference?: string;
  bookingFailed?: boolean;
  bookingEmail?: string;
  bookingStartUtc?: string;
  bookingCallerTimezone?: string;
  bookingCancelUrl?: string;
  bookingRescheduleUrl?: string;
  servicesShown: string[];
  savingsShown?: number;
  provider?: string;
  escalated: boolean;
}

const SLOT_LABEL: Record<string, string> = {
  name: 'Name',
  email: 'Email',
  organisation: 'Organisation',
  phone: 'Phone',
  window: 'Preferred window',
  topic: 'Topic',
};

const KEY_LABEL: Record<string, string> = {
  industry: 'Industry',
  team_size: 'Team size',
  pillar: 'Pillar',
  tier: 'Tier',
  location: 'Location',
};

/**
 * End-of-call summary card. Appears automatically on session close (if
 * anything substantive happened) and shows a snapshot of the conversation.
 */
export class CallSummary {
  private readonly root: HTMLElement;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.classList.add('summary-overlay');
    this.root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t === this.root || t.closest('[data-action="close"]')) {
        this.hide();
        document.dispatchEvent(new CustomEvent('merlin:nav-close', { bubbles: true }));
        return;
      }
      if (t.closest('[data-action="back"]')) {
        document.dispatchEvent(new CustomEvent('merlin:nav-back', { bubbles: true }));
      }
    });
  }

  show(data: CallSummaryData) {
    const duration = this.formatDuration(data.startedAt, data.endedAt);

    const contextRows = data.context
      .map(
        (t) => `
          <li><span class="summary-key">${escape(KEY_LABEL[t.key] ?? t.key)}</span><span class="summary-value">${escape(t.value)}</span></li>`,
      )
      .join('');

    const captureRows = data.captures
      .map(
        (c) => `
          <li><span class="summary-key">${escape(SLOT_LABEL[c.slot] ?? c.slot)}</span><span class="summary-value">${escape(c.value)}</span></li>`,
      )
      .join('');

    const servicesRow = data.servicesShown.length
      ? `<li><span class="summary-key">Walked through</span><span class="summary-value">${escape(data.servicesShown.join(', '))}</span></li>`
      : '';

    const savingsRow =
      data.savingsShown != null
        ? `<li><span class="summary-key">Est. annual saving</span><span class="summary-value">$${data.savingsShown.toLocaleString()}</span></li>`
        : '';

    let bookingTime = '';
    if (data.bookingStartUtc && data.bookingCallerTimezone) {
      const fmt = new Intl.DateTimeFormat('en-AU', {
        timeZone: data.bookingCallerTimezone,
        weekday: 'short', day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
      });
      bookingTime = fmt.format(new Date(data.bookingStartUtc));
    }

    const bookingBlock = data.bookingReference && data.bookingFailed
      ? `
          <div class="summary-cta escalation">
            <div>
              <div class="summary-cta-label">Booking pending</div>
              <div class="summary-cta-ref">${escape(data.bookingReference)}</div>
              <div class="summary-cta-email">The team will follow up with a booking link.</div>
            </div>
          </div>`
      : data.bookingReference
      ? `
          <div class="summary-cta">
            <div>
              <div class="summary-cta-label">Booking confirmed</div>
              <div class="summary-cta-ref">${escape(data.bookingReference)}</div>
              ${bookingTime ? `<div class="summary-cta-email">${escape(bookingTime)}</div>` : ''}
              ${data.bookingEmail ? `<div class="summary-cta-email">Confirmation sent to ${escape(data.bookingEmail)}</div>` : ''}
            </div>
          </div>`
      : data.escalated
        ? `<div class="summary-cta escalation">A human from the team has been paged.</div>`
        : '';

    this.root.innerHTML = `
      <div class="summary-sheet">
        ${panelNavHtml()}
        <div class="summary-header">
          <div class="summary-eyebrow">Today with Merlin</div>
        </div>
        <div class="summary-meta">
          ${duration ? `<span>${escape(duration)}</span>` : ''}
        </div>

        ${
          contextRows
            ? `<section class="summary-section">
                 <h3>Who we spoke with</h3>
                 <ul>${contextRows}</ul>
               </section>`
            : ''
        }

        ${
          captureRows
            ? `<section class="summary-section">
                 <h3>Captured details</h3>
                 <ul>${captureRows}</ul>
               </section>`
            : ''
        }

        ${
          servicesRow || savingsRow
            ? `<section class="summary-section">
                 <h3>Discussed</h3>
                 <ul>${servicesRow}${savingsRow}</ul>
               </section>`
            : ''
        }

        ${bookingBlock}

        <div class="summary-foot">
          <button class="btn-secondary" data-action="close">Close</button>
        </div>
      </div>
    `;
    this.root.classList.add('visible');
  }

  hide() {
    this.root.classList.remove('visible');
  }

  private formatDuration(start?: Date, end?: Date): string {
    if (!start || !end) return '';
    const ms = end.getTime() - start.getTime();
    if (ms < 1000) return '';
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s} second${s === 1 ? '' : 's'}`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return rem === 0 ? `${m} min` : `${m} min ${rem}s`;
  }
}

function escape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

